var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed } from "mobx";
import { buildWebsiteUrl, WebsiteURL } from "healthex-common/websiteurls";
import { i18nGetCurrentLanguage } from "../Infra/i18next";
import { TranscodeStatus } from "healthex-common/transcode.dtos";
import { NOT_LOADED } from "../Infra/RemoteData";
import { ContactMethod } from "healthex-common/consentconfig.dtos";
export class PatientConsentBaseViewModel {
    constructor(container) {
        this.container = container;
        this.environment = container.environment;
        this.patientContactsUIManager = container.patientContactsUIManager;
        this.patientConsentUIManager = container.patientConsentUIManager;
        this.authManager = container.authManager;
        this.analyticsService = container.analyticsService;
    }
    get isScreenshotMode() {
        return this.patientConsentUIManager.isScreenshotMode;
    }
    get isPreview() {
        return this.patientConsentUIManager.isInteractivePreview;
    }
    get screenShotModeMp4Url() {
        var _a, _b;
        if (!this.isScreenshotMode)
            return null;
        return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.mp4Url) !== null && _b !== void 0 ? _b : null;
    }
    get screenShotModePosterUrl() {
        var _a, _b;
        if (!this.isScreenshotMode)
            return null;
        return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.posterUrl) !== null && _b !== void 0 ? _b : null;
    }
    get hasScreenshotPoster() {
        if (!this.isScreenshotMode)
            return false;
        return !!this.screenShotModeMp4Url && !!this.screenShotModePosterUrl;
    }
    get showVideoProcessingPreviewUI() {
        if (!this.isPreview)
            return false;
        const videoTranscode = this.patientConsentUIManager.videoTranscode;
        return (!!videoTranscode &&
            (videoTranscode.status === TranscodeStatus.UPLOADING || videoTranscode.status === TranscodeStatus.TRANSCODING));
    }
    get consentFlowPageData() {
        if (!this.isScreenshotMode)
            return null;
        return this.patientConsentUIManager.consentFlowPageData;
    }
    get studyType() {
        return this.patientConsentUIManager.studyType;
    }
    get prescreenOrEnrollmentStudy() {
        return this.patientConsentUIManager.session.study;
    }
    get prescreenOrEnrollmentFlow() {
        return this.patientConsentUIManager.patientConsentFlow;
    }
    get patientName() {
        var _a, _b;
        return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.patientName) !== null && _b !== void 0 ? _b : this.patientConsentUIManager.patientName;
    }
    get isParentGuardian() {
        return this.patientConsentUIManager.isParentGuardian;
    }
    get parentGuardianName() {
        var _a, _b;
        return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.parentGuardianSignerName) !== null && _b !== void 0 ? _b : this.patientConsentUIManager.parentGuardianName;
    }
    get additionalParentGuardianName() {
        var _a, _b;
        return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.additionalParentGuardianSignerName) !== null && _b !== void 0 ? _b : this.patientConsentUIManager.additionalParentGuardianName;
    }
    get patientMRN() {
        var _a, _b;
        return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.patientMRN) !== null && _b !== void 0 ? _b : this.patientConsentUIManager.patientMRN;
    }
    get providerName() {
        var _a, _b;
        return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.providerSignerName) !== null && _b !== void 0 ? _b : this.patientConsentUIManager.providerName;
    }
    get preferredContactMethod() {
        if (this.isPreview)
            return ContactMethod.EMAIL;
        if (this.isScreenshotMode && !this.isPreview)
            return this.patientConsentUIManager.session.contactMethod;
        return this.patientContactsUIManager.preferredContactMethod;
    }
    get patientEmail() {
        if (this.isPreview)
            return "sample_patient@example.com";
        if (this.isScreenshotMode && !this.isPreview)
            return this.patientConsentUIManager.session.contactEmail;
        return this.patientContactsUIManager.patientEmail;
    }
    get patientPhone() {
        if (this.isPreview)
            return "123-456-7890";
        if (this.isScreenshotMode && !this.isPreview)
            return this.patientConsentUIManager.session.contactPhone;
        return this.patientContactsUIManager.patientPhone;
    }
    get require21CFRPart11ForInformedConsents() {
        return this.patientConsentUIManager.require21CFRPart11ForInformedConsents;
    }
    get protectYourPrivacyButtonEnabled() {
        return !this.patientConsentUIManager.session.isSubmittingConsent;
    }
    get isHeaderAndFooterFixed() {
        return !this.patientConsentUIManager.isScreenshotMode;
    }
    get currentPage() {
        return this.patientConsentUIManager.currentPage;
    }
    get projectLogoUrl() {
        var _a, _b;
        if (this.patientConsentUIManager.session.study !== NOT_LOADED &&
            ((_a = this.patientConsentUIManager.session.study) === null || _a === void 0 ? void 0 : _a.orgLogoUrlOnInformedConsent)) {
            return this.patientConsentUIManager.session.study.orgLogoUrlOnInformedConsent;
        }
        if (this.patientConsentUIManager.studyInfo !== NOT_LOADED && ((_b = this.patientConsentUIManager.studyInfo) === null || _b === void 0 ? void 0 : _b.addedByOrganization.logoUrl)) {
            return this.patientConsentUIManager.studyInfo.addedByOrganization.logoUrl;
        }
        return undefined;
    }
    get projectIconUrl() {
        if (this.patientConsentUIManager.studyInfo !== NOT_LOADED && this.patientConsentUIManager.studyInfo !== null) {
            return this.patientConsentUIManager.studyInfo.organizationIconUrl;
        }
        return null;
    }
    get organizationName() {
        if (this.patientConsentUIManager.studyInfo !== NOT_LOADED && this.patientConsentUIManager.studyInfo !== null) {
            return this.patientConsentUIManager.studyInfo.addedByOrganization.name;
        }
        return "";
    }
    get aiToolName() {
        return this.patientConsentUIManager.aiToolName;
    }
    get animate() {
        return !this.isScreenshotMode;
    }
    get isAIFlow() {
        return this.patientConsentUIManager.isAIFlow;
    }
    get idpHint() {
        return this.patientConsentUIManager.idpHint;
    }
    get idpHintProviderDisplayName() {
        var _a;
        const hint = (_a = this.idpHint) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        if (!hint)
            return null;
        if (hint.includes("google")) {
            return "Google";
        }
        else if (hint.includes("apple")) {
            return "Apple";
        }
        else if (hint.includes("microsoft")) {
            return "Microsoft";
        }
        return null;
    }
    goToNextPage() {
        this.patientConsentUIManager.goToNextPage();
    }
    goToPrivacyPolicy() {
        if (this.isPreview)
            return;
        window.open(buildWebsiteUrl(WebsiteURL.PRIVACY, this.environment.WEBSITE_BASE_URL, i18nGetCurrentLanguage()));
    }
}
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "isScreenshotMode", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "isPreview", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "screenShotModeMp4Url", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "screenShotModePosterUrl", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "hasScreenshotPoster", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "showVideoProcessingPreviewUI", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "consentFlowPageData", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "studyType", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "prescreenOrEnrollmentStudy", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "prescreenOrEnrollmentFlow", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "patientName", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "isParentGuardian", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "parentGuardianName", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "additionalParentGuardianName", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "patientMRN", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "providerName", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "preferredContactMethod", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "patientEmail", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "patientPhone", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "require21CFRPart11ForInformedConsents", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "protectYourPrivacyButtonEnabled", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "isHeaderAndFooterFixed", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "currentPage", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "projectLogoUrl", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "projectIconUrl", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "organizationName", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "aiToolName", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "animate", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "isAIFlow", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "idpHint", null);
__decorate([
    computed
], PatientConsentBaseViewModel.prototype, "idpHintProviderDisplayName", null);
